import { SearchOutlined, DownloadOutlined } from "@ant-design/icons";
import { Button, Input, Select, Space, Table } from "antd";
import axios from "axios";
import moment from "moment";
import React, { useRef, useState } from "react";
import { useQuery } from "react-query";
import { useNavigate } from "react-router-dom";
import { serverUrl } from "../../../../../nestserver";
import CombinedProgressCards from "../../../../../components/CombinedProgressCards";

const TemplateList = () => {
  const { Option } = Select;
  const navigate = useNavigate();
  const searchInputRef = useRef(null);
  const [studentId, setStudentId] = useState(null);

  const handleDownload = async (id, templateName) => {
    try {
      const res = await axios({
        method: "get",
        url: `${serverUrl}/progress-card-v2/${id}/download`,
        headers: {
          Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
        },
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `ProgressCard_${templateName || "Report"}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch (error) {
      console.log("Error downloading PDF:", error);
    }
  };

  const handleSelectChange = (value) => {
    setStudentId(value);
  };

  //fetch student
  async function fetchParentStudent() {
    const res = await axios({
      method: "get",
      url: `${serverUrl}/students/by-parent/`,
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
      },
    });
    return res.data.result;
  }
  const students = useQuery("students/by-parent", fetchParentStudent);

  const getStudent = useQuery(
    ["progress-card-v2", studentId],
    () =>
      axios({
        method: "get",
        url: `${serverUrl}/progress-card-v2/by-student/?studentId=${studentId}`,
        headers: {
          Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
        },
      }).then((res) => res.data),
    {
      enabled: !!studentId,
    },
  );

  // Table Settings

  // Search Filter

  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");

  const getNestedValue = (record, path) =>
    path.split(".").reduce((value, key) => value?.[key], record);

  const highlightMatch = (text, highlight) => {
    if (!highlight || !text) return text;
    const escaped = highlight.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    return text
      .toString()
      .split(new RegExp(`(${escaped})`, "gi"))
      .map((part, i) =>
        part.toLowerCase() === highlight.toLowerCase() ? (
          <mark key={i} style={{ backgroundColor: "#ffe58f", padding: 0 }}>
            {part}
          </mark>
        ) : (
          part
        ),
      );
  };

  const getColumnSearchProps = (dataIndex, label) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={searchInputRef}
          placeholder={`Search ${label || dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={
              <SearchOutlined
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              />
            }
            size="small"
            style={{ width: 90, display: "inline-flex", alignItems: "center", justifyContent: "center" }}
          >
            Search
          </Button>
          <Button
            onClick={() => handleReset(clearFilters)}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
    ),
    onFilter: (value, record) => {
      const fieldValue = getNestedValue(record, dataIndex);
      return fieldValue
        ? fieldValue.toString().toLowerCase().includes(value.toLowerCase())
        : false;
    },
    onFilterDropdownVisibleChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInputRef.current?.select(), 100);
      }
    },
  });

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText("");
  };

  const columns = [
    {
      title: "Template",
      dataIndex: [],
      key: "",
      align: "center",
      ...getColumnSearchProps("template.name", "Template"),
      render: (record) => (
        <div
          style={{ color: "#111111" }}
          className="text-base text-center capitalize"
        >
          {searchedColumn === "template.name"
            ? highlightMatch(record?.template?.name, searchText)
            : record?.template?.name}
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: [],
      key: "",
      align: "center",
      render: (record) => (
        <div
          key={record}
          style={{ color: "#111111" }}
          className="text-base text-center capitalize"
        >
          {record?.status}
        </div>
      ),
    },
    {
      title: "Last Report",
      dataIndex: [],
      key: "",
      align: "center",
      render: (record) => (
        <div
          key={record}
          style={{ color: "#111111" }}
          className="text-base text-center capitalize"
        >
          {record?.status === "notified" && record?.notified_at
            ? moment(record?.notified_at).format("DD MMMM YYYY,hh:mm A")
            : "--"}
        </div>
      ),
    },
    {
      title: "Action",
      dataIndex: [],
      key: "",
      align: "center",
      render: (record) => (
        <Space size="middle">
          <div
            className="text-base font-semibold text-center text-blue-500 cursor-pointer"
            key={record}
            onClick={() => {
              navigate(
                `/dashboard/parent/menu/template/report-card/${record?._id}`,
              );
            }}
          >
            View
          </div>
          <div
            className="text-base font-semibold text-center text-orange-500 cursor-pointer flex items-center gap-1"
            onClick={() => handleDownload(record?._id, record?.template?.name || "Report")}
          >
            <DownloadOutlined /> Download
          </div>
        </Space>
      ),
    },
  ];

  return (
    <>
      <div className="p-8">
        <div
          style={{ borderRadius: "10px", color: "#616161" }}
          className=" bg-white drop-shadow-md p-8 w-full "
        >
          <div className="text-2xl text-orange-500 font-semibold pb-8">
            Report
          </div>
          <div className="flex gap-4" style={{ width: "30%" }}>
            <h5 className="text-base font-semibold">Name</h5>
            <Select
              showSearch
              showArrow
              placeholder="Select name"
              className="w-full bg-gray-300"
              style={{
                border: "6px",
                backgroundColor: "#EBEBEB",
              }}
              getPopupContainer={(trigger) => trigger.parentNode}
              onChange={(value) => handleSelectChange(value)}
            >
              {students &&
                students?.data?.map((data) => {
                  return (
                    <Option key={data._id} value={data._id}>
                      {`${data?.first_name} ${data?.last_name}`}
                    </Option>
                  );
                })}
            </Select>
          </div>
          <div className="py-5">
            <Table
              rowKey="_id"
              columns={columns}
              dataSource={
                getStudent?.isSuccess && getStudent?.data?.result
                  ? getStudent?.data?.result
                  : []
              }
              pagination={true}
            />
          </div>
          <CombinedProgressCards studentId={studentId} canAcknowledge />
        </div>
      </div>
    </>
  );
};

export default TemplateList;
